// Service worker registration helper.
//
// vite-plugin-pwa generates the SW from src/sw/sw.ts. We register it manually
// (rather than letting the plugin auto-register) so we control WHEN a new
// version takes over.
//
// An updated worker installs in the background and waits (see the lifecycle
// section of sw/sw.ts and `registerType: 'prompt'` in vite.config.ts). When it
// is ready, `onNeedRefresh` hands it to lib/appUpdate.ts, which decides how it
// is applied: silently at the start of a session when nothing is being
// edited, otherwise through the "Update available" card (IMP-064).
//
// History: until IMP-064 this file applied the update whenever the tab was
// hidden, guarded by a `window.__hasUnsavedChanges` flag that no form ever set
// — so it could reload over a half-filled form, and on phones (where a
// backgrounded page is frozen) it often never ran at all. Forms now report
// unsaved changes through lib/unsavedChanges.ts.

import { registerSW } from 'virtual:pwa-register';

import { onNewVersionInControl, onUpdateReady, setUpdateRegistration } from './appUpdate';

export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;

  const updateSW = registerSW({
    immediate: false,
    onNeedRefresh() {
      // A new version is installed and waiting. `updateSW` posts SKIP_WAITING
      // to it; once it takes control the plugin calls onNeedReload below.
      onUpdateReady(() => updateSW(true));
    },
    onNeedReload() {
      // The new version controls this page now — after our own Reload, or
      // because another tab applied it. appUpdate reloads unless this tab has
      // unsaved work, in which case the card offers the reload instead.
      onNewVersionInControl();
    },
    onOfflineReady() {
      // eslint-disable-next-line no-console
      console.info('[sw] app is ready to work offline');
    },
    onRegisteredSW(swScriptUrl, registration) {
      // Lets appUpdate check for a new version on return and every hour.
      setUpdateRegistration(registration);
      // eslint-disable-next-line no-console
      console.info('[sw] registered:', swScriptUrl);
    },
    onRegisterError(err) {
      // eslint-disable-next-line no-console
      console.warn('[sw] registration error:', err);
    },
  });

  // Expose for debug: window.__updateSW() applies a waiting update now.
  (window as unknown as { __updateSW?: () => Promise<void> }).__updateSW = updateSW;
}
